import { useEffect, useState } from "react";
import { HelpCircle, PenLine } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AiHelp } from "./AiHelp";

const AnswerInput = ({ f, value, onSave }) => {
  const [local, setLocal] = useState(value ?? "");
  useEffect(() => setLocal(value ?? ""), [value]);
  const tid = `draft-answer-${f.key}`;
  if (f.kind === "choice") return (
    <Select value={value || ""} onValueChange={(v) => onSave(v)}>
      <SelectTrigger data-testid={tid} className="h-11 bg-white"><SelectValue placeholder="Choose an answer" /></SelectTrigger>
      <SelectContent>{f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  if (f.kind === "checkbox") return (
    <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!value} onCheckedChange={(v) => onSave(!!v)} data-testid={tid} /> Check this box</label>
  );
  return <Input data-testid={tid} type={f.kind === "date" ? "date" : "text"} value={local} onChange={(e) => setLocal(e.target.value)}
    onBlur={() => local !== (value ?? "") && onSave(local)} className="h-11 bg-white" />;
};

export const DraftField = ({ f, draft, onSave, onUnsure, isSample }) => {
  const value = draft.answers[f.key];
  const suggestion = draft.suggestions[f.key];
  const unsure = f.key in draft.unsure;
  return (
    <div data-testid={`draft-field-${f.key}`} className="grid gap-4 rounded-xl border bg-white p-5 lg:grid-cols-[1fr_1.2fr_1fr]">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-wider text-gold-dark">{f.number}</p>
        <p className="mt-1 text-sm font-medium text-navy">{f.label}</p>
        <p className="mt-1 text-[11px] text-slate-400">Wording from the form{f.required ? " · Needed" : f.required_if ? " · Needed in some cases" : " · Optional"}</p>
      </div>
      <div className="space-y-2 text-sm text-slate-700">
        <p>{f.explanation}</p>
        {f.example && <p data-testid={`draft-example-${f.key}`} className="rounded bg-gold-light/50 px-2 py-1 text-xs text-slate-600"><strong>Fictional example, not your answer:</strong> {f.example}</p>}
        {f.questions?.length > 0 && <ul className="space-y-0.5 text-xs text-slate-500">{f.questions.map((q) => <li key={q} className="flex gap-1"><HelpCircle className="mt-0.5 h-3 w-3 shrink-0" />{q}</li>)}</ul>}
        {isSample ? <p className="text-[11px] text-slate-400" data-testid={`ai-help-sample-off-${f.key}`}>AI help is turned off in the sample workspace.</p>
          : <AiHelp workspaceId={draft.workspace_id} draftId={draft.id} field={f} />}
      </div>
      <div className="space-y-2">
        {f.kind === "self" ? (
          <p data-testid={`draft-self-${f.key}`} className="flex gap-2 rounded-md border border-dashed p-3 text-xs text-slate-600"><PenLine className="h-4 w-4 shrink-0" /> You complete this yourself on the official form.</p>
        ) : <>
          <AnswerInput f={f} value={value} onSave={(v) => onSave(f.key, v)} />
          {suggestion && (
            <div data-testid={`draft-suggestion-${f.key}`} className="rounded-md border border-navy/15 bg-navy/5 p-2 text-xs text-navy">
              Use a saved detail from {suggestion.source}? <strong>{suggestion.value}</strong>
              <button type="button" onClick={() => onSave(f.key, suggestion.value)} data-testid={`draft-suggestion-use-${f.key}`} className="ml-2 font-medium underline">Use this</button>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Checkbox checked={unsure} onCheckedChange={(v) => onUnsure(f.key, v ? "" : null)} data-testid={`draft-unsure-${f.key}`} /> I'm not sure about this yet
          </label>
        </>}
      </div>
    </div>
  );
};
